import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  Eye,
  ChevronDown,
  Search,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Tournament {
  id: number;
  name: string;
  creator: number;
  status: string;
  participants: number[];
  maxPlayers: number;
  startDate?: string;
}

export function TournamentsTab() {
  const [, setLocation] = useLocation();
  const {
    data: tournaments,
    isLoading,
    refetch,
  } = useQuery<Tournament[]>({
    queryKey: ["admin-tournaments"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/tournaments");
      return response as Tournament[];
    },
  });

  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Mutation for updating tournament status
  const updateTournamentMutation = useMutation({
    mutationFn: async ({
      tournamentId,
      status,
    }: {
      tournamentId: number;
      status: string;
    }) => {
      const response = await apiRequest(`/api/admin/tournaments/${tournamentId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      return response;
    },
    onSuccess: () => {
      toast({
        title: "Tournament updated",
        description: "Tournament status has been successfully updated",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating tournament",
        description: error.message ?? "An error occurred while updating the tournament",
        variant: "destructive",
      });
    },
  });

  const handleViewTournament = (tournamentId: number) => {
    setLocation(`/admin/tournament/${tournamentId}`);
  };

  const handleUpdateStatus = (tournamentId: number, status: string) => {
    updateTournamentMutation.mutate({ tournamentId, status });
  };

  // Filter tournaments based on search query and status filter
  const filteredTournaments = tournaments
    ? tournaments.filter((tournament: Tournament) => {
        // Search filter
        const matchesSearch = [
          tournament.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          tournament.creator?.toString().includes(searchQuery) ?? false,
          tournament.id?.toString().includes(searchQuery) ?? false
        ].some(Boolean);

        // Status filter
        const matchesStatus =
          statusFilter === "all" || statusFilter === tournament.status;

        return matchesSearch && matchesStatus;
      })
    : [];

  if (isLoading) {
    return <div className="text-center py-8">Loading tournaments...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">Tournament Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, ID or creator..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
            }}
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Reset Filters
          </Button>
        </div>
      </div>

      {!tournaments || tournaments.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No tournaments found
        </div>
      ) : filteredTournaments.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No tournaments match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Creator</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Players</th>
                <th className="text-left p-2">Start Date</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTournaments.map((tournament) => (
                <tr key={tournament.id} className="border-t hover:bg-muted/50">
                  <td className="p-2">{tournament.id}</td>
                  <td className="p-2">{tournament.name}</td>
                  <td className="p-2">{tournament.creator}</td>
                  <td className="p-2">
                    <Badge
                      className={
                        tournament.status === "completed"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : tournament.status === "active"
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : tournament.status === "cancelled"
                              ? "bg-red-600 hover:bg-red-700 text-white"
                              : "bg-gray-600 hover:bg-gray-700 text-white"
                      }
                    >
                      {tournament.status}
                    </Badge>
                  </td>
                  <td className="p-2">
                    {Array.isArray(tournament.participants)
                      ? tournament.participants.length
                      : 0}
                    /{tournament.maxPlayers}
                  </td>
                  <td className="p-2">
                    {tournament.startDate
                      ? new Date(tournament.startDate).toLocaleDateString()
                      : "Not Started"}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewTournament(tournament.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            Status <ChevronDown className="h-4 w-4 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Change Status</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "pending")
                            }
                            disabled={tournament.status === "pending"}
                          >
                            Set to Pending
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "active")
                            }
                            disabled={
                              tournament.status === "active" ||
                              tournament.status === "completed"
                            }
                          >
                            Set to Active
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "completed")
                            }
                            disabled={tournament.status === "completed"}
                          >
                            Set to Completed
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(tournament.id, "cancelled")
                            }
                            disabled={tournament.status === "cancelled"}
                          >
                            Set to Cancelled
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}